import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import MainSidebar from '../../components/ui/MainSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SignatureCanvas from '../work-execution/components/SignatureCanvas';
import { vehicleDeliveryService } from '../../services/vehicleDeliveryService';
import { workOrderService } from '../../services/workOrderService';
import { toolsAccessoriesService } from '../../services/toolsAccessoriesService';
import { useAuth } from '../../contexts/AuthContext';

const VehicleDeliveryConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Work order state
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(true);
  
  // Delivery form state
  const [signatureData, setSignatureData] = useState(null);
  const [finalOdometer, setFinalOdometer] = useState('');
  const [keysChecklist, setKeysChecklist] = useState({});
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState([]);
  const [satisfactionRating, setSatisfactionRating] = useState(0);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [odometerVariance, setOdometerVariance] = useState(null);

  useEffect(() => {
    fetchCompletedWorkOrders();
    
    // Load from navigation state if available
    if (location?.state?.workOrder) {
      setSelectedWorkOrder(location?.state?.workOrder);
      loadKeysChecklist(location?.state?.workOrder?.id);
    }
  }, [location]);

  useEffect(() => {
    if (selectedWorkOrder && finalOdometer) {
      const initialOdometer = selectedWorkOrder?.kilometraje || 0;
      const variance = parseInt(finalOdometer) - initialOdometer;
      setOdometerVariance(variance);
    }
  }, [finalOdometer, selectedWorkOrder]);

  const fetchCompletedWorkOrders = async () => {
    try {
      setLoadingWorkOrders(true);
      const orders = await workOrderService?.getAll();
      const completedOrders = orders?.filter(order => order?.estadoOt === 'completado');
      setWorkOrders(completedOrders || []);
    } catch (error) {
      console.error('Error fetching work orders:', error);
      setError('Error al cargar órdenes de trabajo');
    } finally {
      setLoadingWorkOrders(false);
    }
  };

  const loadKeysChecklist = async (workOrderId) => {
    try {
      const items = await toolsAccessoriesService?.getByWorkOrderId(workOrderId);
      
      // Convert tools/accessories to checklist format
      const checklist = {};
      items?.forEach(item => {
        if (!checklist?.[item?.category]) {
          checklist[item?.category] = {};
        }
        checklist[item?.category][item?.id] = {
          checked: false,
          itemName: item?.itemName,
          quantity: item?.quantity
        };
      });
      
      setKeysChecklist(checklist);
    } catch (error) {
      console.error('Error loading keys checklist:', error);
    }
  };

  const handleWorkOrderSelect = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setFinalOdometer(workOrder?.kilometraje?.toString() || '');
    loadKeysChecklist(workOrder?.id);
    setError('');
  };

  const handleSignatureSave = (signature) => {
    setSignatureData(signature);
    setError('');
  };

  const handleSignatureClear = () => {
    setSignatureData(null);
  };

  const handlePhotoUpload = (event) => {
    const files = Array.from(event?.target?.files || []);
    if (files?.length === 0) return;

    // Create preview URLs
    const newPreviewUrls = files?.map(file => URL.createObjectURL(file));
    setPhotoPreviewUrls(prev => [...prev, ...newPreviewUrls]);
    setPhotoFiles(prev => [...prev, ...files]);
  };

  const handleRemovePhoto = (index) => {
    URL.revokeObjectURL(photoPreviewUrls?.[index]);
    setPhotoPreviewUrls(prev => prev?.filter((_, i) => i !== index));
    setPhotoFiles(prev => prev?.filter((_, i) => i !== index));
  };

  const handleChecklistToggle = (categoryId, itemId) => {
    setKeysChecklist(prev => ({
      ...prev,
      [categoryId]: {
        ...prev?.[categoryId],
        [itemId]: {
          ...prev?.[categoryId]?.[itemId],
          checked: !prev?.[categoryId]?.[itemId]?.checked
        }
      }
    }));
  };

  const validateForm = () => {
    if (!selectedWorkOrder) {
      setError('Seleccione una orden de trabajo');
      return false;
    }

    if (!signatureData) {
      setError('Se requiere firma del cliente');
      return false;
    }

    if (!finalOdometer || finalOdometer === '') {
      setError('Ingrese el kilometraje final');
      return false;
    }

    const finalOdometerNum = parseInt(finalOdometer);
    if (isNaN(finalOdometerNum) || finalOdometerNum < 0) {
      setError('Kilometraje final inválido');
      return false;
    }

    // Check if all keys are returned
    const allKeysReturned = Object.values(keysChecklist)?.every(category =>
      Object.values(category)?.every(item => item?.checked)
    );

    if (!allKeysReturned) {
      setError('Todos los artículos deben ser verificados y devueltos');
      return false;
    }

    if (photoFiles?.length === 0) {
      setError('Se requiere al menos una foto de evidencia');
      return false;
    }

    if (satisfactionRating === 0) {
      setError('Seleccione una calificación de satisfacción');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      setError('');

      // Upload photos
      setUploadingPhotos(true);
      const uploadedPhotoPaths = [];
      for (const file of photoFiles) {
        const path = await vehicleDeliveryService?.uploadPhoto(file, selectedWorkOrder?.id);
        uploadedPhotoPaths?.push(path);
      }
      setUploadingPhotos(false);

      // Create delivery record
      const deliveryData = {
        workOrderId: selectedWorkOrder?.id,
        customerSignature: signatureData,
        finalOdometer: parseInt(finalOdometer),
        keysChecklist: keysChecklist,
        photoEvidence: uploadedPhotoPaths,
        satisfactionRating: satisfactionRating,
        deliveryNotes: deliveryNotes
      };

      await vehicleDeliveryService?.create(deliveryData);

      // Update work order status to 'entregado'
      await workOrderService?.updateStatus(selectedWorkOrder?.id, 'entregado');

      // Create timeline event
      await workOrderService?.createTimelineEvent({
        workOrderId: selectedWorkOrder?.id,
        stage: 'entrega',
        status: 'Vehículo entregado al cliente',
        findings: `Kilometraje final: ${finalOdometer}km. Calificación: ${satisfactionRating}/5 estrellas.`
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/workshop-dashboard');
      }, 2000);
    } catch (error) {
      console.error('Error submitting delivery:', error);
      setError('Error al registrar la entrega. Intente nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCategoryIcon = (categoryId) => {
    const icons = {
      keys: 'Key',
      spare: 'Wrench',
      documentation: 'FileText',
      personal: 'Briefcase',
      electronics: 'Smartphone'
    };
    return icons?.[categoryId] || 'Package';
  };

  const getCategoryLabel = (categoryId) => {
    const labels = {
      keys: 'Llaves y Controles',
      spare: 'Refacciones y Herramientas',
      documentation: 'Documentación',
      personal: 'Artículos Personales',
      electronics: 'Electrónicos'
    };
    return labels?.[categoryId] || categoryId;
  };

  return (
    <>
      <Helmet>
        <title>Confirmación de Entrega - GearControl</title>
      </Helmet>
      
      <div className="flex h-screen bg-background">
        <MainSidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
        
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="bg-white border-b border-border px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Confirmación de Entrega</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Registro de entrega con firma, kilometraje y verificación de artículos
                </p>
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto p-6">
            {success ? (
              <div className="max-w-2xl mx-auto">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <Icon name="CheckCircle" className="w-16 h-16 text-green-600 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-green-900 mb-2">¡Entrega Registrada!</h2>
                  <p className="text-green-700">La entrega del vehículo se ha completado exitosamente.</p>
                </div>
              </div>
            ) : (
              <div className="max-w-5xl mx-auto space-y-6">
                {/* Work Order Selection */}
                <div className="bg-white rounded-lg shadow-sm border border-border p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="FileText" className="w-5 h-5" />
                    Seleccionar Orden de Trabajo
                  </h2>
                  
                  {loadingWorkOrders ? (
                    <div className="text-center py-8">
                      <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary mx-auto" />
                      <p className="text-muted-foreground mt-2">Cargando órdenes...</p>
                    </div>
                  ) : workOrders?.length === 0 ? (
                    <div className="text-center py-8">
                      <Icon name="AlertCircle" className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                      <p className="text-muted-foreground">No hay órdenes completadas disponibles</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {workOrders?.map(order => (
                        <div
                          key={order?.id}
                          onClick={() => handleWorkOrderSelect(order)}
                          className={`p-4 border rounded-lg cursor-pointer transition-all ${
                            selectedWorkOrder?.id === order?.id
                              ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-semibold text-foreground">{order?.folioOt}</p>
                              <p className="text-sm text-muted-foreground mt-1">
                                {order?.vehicle?.marca} {order?.vehicle?.modelo} - {order?.vehicle?.placas}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Cliente: {order?.client?.nombre}
                              </p>
                            </div>
                            {selectedWorkOrder?.id === order?.id && (
                              <Icon name="CheckCircle" className="w-5 h-5 text-primary" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {selectedWorkOrder && (
                  <>
                    {/* Vehicle Summary */}
                    <div className="bg-white rounded-lg shadow-sm border border-border p-6">
                      <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                        <Icon name="Car" className="w-5 h-5" />
                        Resumen del Vehículo
                      </h2>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Folio</p>
                          <p className="font-semibold text-foreground">{selectedWorkOrder?.folioOt}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Vehículo</p>
                          <p className="font-semibold text-foreground">
                            {selectedWorkOrder?.vehicle?.marca} {selectedWorkOrder?.vehicle?.modelo}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Placas</p>
                          <p className="font-semibold text-foreground">{selectedWorkOrder?.vehicle?.placas}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Kilometraje Inicial</p>
                          <p className="font-semibold text-foreground">{selectedWorkOrder?.kilometraje?.toLocaleString()} km</p>
                        </div>
                      </div>
                    </div>

                    {/* Customer Signature */}
                    <div className="bg-white rounded-lg shadow-sm border border-border p-6">
                      <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                        <Icon name="PenTool" className="w-5 h-5" />
                        Firma del Cliente
                      </h2>
                      <SignatureCanvas onSave={handleSignatureSave} onClear={handleSignatureClear} />
                    </div>

                    {/* Final Odometer */}
                    <div className="bg-white rounded-lg shadow-sm border border-border p-6">
                      <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                        <Icon name="Gauge" className="w-5 h-5" />
                        Kilometraje Final
                      </h2>
                      <div className="max-w-md">
                        <Input
                          type="number"
                          value={finalOdometer}
                          onChange={(e) => setFinalOdometer(e?.target?.value)}
                          placeholder="Ingrese kilometraje final"
                          className="mb-2"
                        />
                        {odometerVariance !== null && (
                          <div className={`flex items-center gap-2 text-sm ${
                            Math.abs(odometerVariance) > 100 ? 'text-orange-600' : 'text-muted-foreground'
                          }`}>
                            <Icon name="Info" className="w-4 h-4" />
                            <span>
                              Variación: {odometerVariance > 0 ? '+' : ''}{odometerVariance} km
                              {Math.abs(odometerVariance) > 100 && ' (Variación significativa)'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Keys Return Checklist */}
                    <div className="bg-white rounded-lg shadow-sm border border-border p-6">
                      <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                        <Icon name="ClipboardCheck" className="w-5 h-5" />
                        Verificación de Artículos Devueltos
                      </h2>
                      
                      {Object.keys(keysChecklist)?.length === 0 ? (
                        <div className="text-center py-8">
                          <Icon name="Package" className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                          <p className="text-muted-foreground">No hay artículos registrados para esta orden</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {Object.entries(keysChecklist)?.map(([categoryId, items]) => (
                            <div key={categoryId} className="border border-border rounded-lg p-4">
                              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                                <Icon name={getCategoryIcon(categoryId)} className="w-4 h-4" />
                                {getCategoryLabel(categoryId)}
                              </h3>
                              <div className="space-y-2">
                                {Object.entries(items)?.map(([itemId, item]) => (
                                  <label
                                    key={itemId}
                                    className="flex items-center gap-3 p-2 hover:bg-muted/50 rounded cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={item?.checked || false}
                                      onChange={() => handleChecklistToggle(categoryId, itemId)}
                                      className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                                    />
                                    <span className="flex-1 text-sm text-foreground">
                                      {item?.itemName}
                                      {item?.quantity > 1 && (
                                        <span className="text-muted-foreground ml-2">(x{item?.quantity})</span>
                                      )}
                                    </span>
                                    {item?.checked && (
                                      <Icon name="Check" className="w-4 h-4 text-green-600" />
                                    )}
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Photo Evidence */}
                    <div className="bg-white rounded-lg shadow-sm border border-border p-6">
                      <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                        <Icon name="Camera" className="w-5 h-5" />
                        Evidencia Fotográfica
                      </h2>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block">
                            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors">
                              <Icon name="Upload" className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                              <p className="text-sm text-muted-foreground">Click para subir fotos</p>
                              <p className="text-xs text-muted-foreground mt-1">Condición del vehículo al momento de entrega</p>
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handlePhotoUpload}
                              className="hidden"
                            />
                          </label>
                        </div>

                        {photoPreviewUrls?.length > 0 && (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {photoPreviewUrls?.map((url, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={url}
                                  alt={`Evidencia ${index + 1}`}
                                  className="w-full h-32 object-cover rounded-lg border border-border"
                                />
                                <button
                                  onClick={() => handleRemovePhoto(index)}
                                  className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <Icon name="X" className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Satisfaction Rating */}
                    <div className="bg-white rounded-lg shadow-sm border border-border p-6">
                      <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                        <Icon name="Star" className="w-5 h-5" />
                        Calificación de Satisfacción
                      </h2>
                      <div className="flex items-center gap-2">
                        {[1, 2, 3, 4, 5]?.map(rating => (
                          <button
                            key={rating}
                            onClick={() => setSatisfactionRating(rating)}
                            className="transition-transform hover:scale-110"
                          >
                            <Icon
                              name="Star"
                              className={`w-10 h-10 ${
                                rating <= satisfactionRating
                                  ? 'text-yellow-400 fill-yellow-400' :'text-gray-300'
                              }`}
                            />
                          </button>
                        ))}
                        {satisfactionRating > 0 && (
                          <span className="ml-4 text-lg font-semibold text-foreground">
                            {satisfactionRating}/5
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Delivery Notes */}
                    <div className="bg-white rounded-lg shadow-sm border border-border p-6">
                      <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                        <Icon name="MessageSquare" className="w-5 h-5" />
                        Notas de Entrega (Opcional)
                      </h2>
                      <textarea
                        value={deliveryNotes}
                        onChange={(e) => setDeliveryNotes(e?.target?.value)}
                        placeholder="Observaciones adicionales sobre la entrega..."
                        rows={4}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                        <Icon name="AlertCircle" className="w-5 h-5 text-red-600" />
                        <p className="text-red-700">{error}</p>
                      </div>
                    )}

                    {/* Submit Button */}
                    <div className="flex items-center justify-end gap-4">
                      <Button
                        variant="outline"
                        onClick={() => navigate('/workshop-dashboard')}
                        disabled={isSubmitting}
                      >
                        Cancelar
                      </Button>
                      <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting || uploadingPhotos}
                        iconName={isSubmitting ? 'Loader2' : 'CheckCircle'}
                        className={isSubmitting ? 'animate-spin' : ''}
                      >
                        {uploadingPhotos ? 'Subiendo fotos...' : isSubmitting ? 'Registrando...' : 'Confirmar Entrega'}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </main>
        </div>
      </div>
    </>
  );
};

export default VehicleDeliveryConfirmation;